import { HttpService, VisibaApiPromise } from '@services/http.service';
import { Singleton } from '@visiba/instantiation';
import { ParameterHandler } from '@visiba/std';
import { ApiBaseClient } from './_api_client_base';
import type { AuthMethodApiModel, SmsConfirmCodeApiResult, SmsStartAuthModel } from './generated/models';

@Singleton()
export class AuthApiService extends ApiBaseClient {
	constructor(private readonly httpService: HttpService) {
		super('units');
	}

	public getMethods(licenceHolderId: number): VisibaApiPromise<
		{
			Scheme: string;
			Name: string;
			Description: string;
			UseIframe: boolean;
		}[]
	> {
		return this.httpService.get(`/client/auth/idp/${licenceHolderId}/methods`);
	}

	public async getAuthMethods(
		unitId: number,
		countryCode: string,
		callTicket: string | null = null,
	): VisibaApiPromise<AuthMethodApiModel[]> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({
			device: 'computer',
			countryCode,
		});
		if (callTicket != null) parameterHandler.append({ usage: 'Outsider' });

		return this.httpService.get(`${this.getBaseUri()}/${unitId}/Auth/Methods${parameterHandler.getString()}`);
	}

	public async sendSMSCode(unitId: number, number: string, countryCode?: string): VisibaApiPromise<SmsStartAuthModel> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ number: number });
		if (countryCode) parameterHandler.append({ countrycode: countryCode });

		return this.httpService.post(`${this.getBaseUri()}/${unitId}/Auth/Methods/Sms/SendCode${parameterHandler.getString()}`, null);
	}

	public async sendSMSCodeWithCallticket(
		unitId: number,
		countryCode: string,
		callTicket: string,
	): VisibaApiPromise<SmsStartAuthModel> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({
			callTicket: callTicket,
			countryCode: countryCode,
		});

		return this.httpService.post(
			`${this.getBaseUri()}/${unitId}/Auth/Methods/Sms/SendCodeWithCallTicket${parameterHandler.getString()}`,
			null,
		);
	}

	public async resendSmsCode(unitId: number, authAttemptId: Guid): VisibaApiPromise<void> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ attemptId: authAttemptId });

		return this.httpService.post(`${this.getBaseUri()}/${unitId}/Auth/Methods/Sms/ReSendCode${parameterHandler.getString()}`, null);
	}

	public async getPreRegisteredNumber(receptionId: number, callTicket: string): VisibaApiPromise<number> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ callticket: callTicket });

		return this.httpService.get(
			`${this.getBaseUri()}/${receptionId}/Auth/Methods/Sms/PreRegisteredNumber${parameterHandler.getString()}`,
		);
	}

	public async confirmSmsCode(
		unitId: number,
		attemptId: Guid,
		code: string,
		sessionLifetime?: number,
	): VisibaApiPromise<SmsConfirmCodeApiResult> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ attemptId: attemptId, code: code });
		if (sessionLifetime) parameterHandler.append({ sessionLifetime: sessionLifetime });

		return this.httpService.post(`${this.getBaseUri()}/${unitId}/Auth/Methods/Sms/ConfirmCode${parameterHandler.getString()}`, null);
	}

	public async beginNoAuth(unitId: number, callTicket: string, countryCode?: string): VisibaApiPromise<SmsStartAuthModel> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ callTicket: callTicket });
		if (countryCode) parameterHandler.append({ countrycode: countryCode });

		return this.httpService.post(`${this.getBaseUri()}/${unitId}/Auth/Methods/NoAuth/StartAuth${parameterHandler.getString()}`, null);
	}

	public async confirmNoAuthIdentity(unitId: number, attemptId: Guid, commonName: string): VisibaApiPromise<SmsConfirmCodeApiResult> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ authAttempt: attemptId, commonName: encodeURIComponent(commonName) });

		return this.httpService.post(
			`${this.getBaseUri()}/${unitId}/Auth/Methods/NoAuth/ConfirmIdentity${parameterHandler.getString()}`,
			null,
		);
	}
}
